import React, { forwardRef } from "react";
import { StatusDot } from "../../atoms/StatusDot/index.js";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import {
  tintClasses,
  type CategoricalTint,
  type ModuleTint,
} from "../../lib/tints.js";

/* ---------------- ChartLegend ----------------
   A chart's series key as a named list. Each item's colour is a decorative
   `tint` (lib/tints.ts) or a semantic `tone` (lib/tones.ts), never both, and
   never a colour string. */

/** `line` (default): a short solid bar. `dot`: a StatusDot. `dashed`: a
 *  short dashed bar, for a target or overlay series. */
export type ChartLegendMarker = "line" | "dot" | "dashed";

interface ChartLegendItemBase {
  /** Stable key; never shown. */
  readonly id: string;
  /** The series name, shown beside its marker. */
  readonly label: string;
  /** Default `line`. */
  readonly marker?: ChartLegendMarker;
  /** Colour is a tint or a tone, never a string. */
  readonly color?: never;
}

/** Decorative `tint` (a `ModuleTint` is fine here: a legend dot is chrome) or
 *  semantic `tone`: the type refuses both. */
type ChartLegendItemColour =
  | { readonly tint: CategoricalTint | ModuleTint; readonly tone?: never }
  | { readonly tone: StatusTone; readonly tint?: never };

export type ChartLegendItem = ChartLegendItemBase & ChartLegendItemColour;

export interface ChartLegendProps extends Omit<
  React.HTMLAttributes<HTMLUListElement>,
  "children" | "color"
> {
  /** The list's accessible name, such as "Chart legend". */
  label: string;
  items: readonly ChartLegendItem[];
}

/* The marker is a picture of the series line; the item's text names it. */
const LINE = "h-0.5 w-3 shrink-0 rounded-full";
const DASHED = "h-0 w-3 shrink-0 border-t-2 border-dashed";

function LegendMarker({
  item,
}: {
  readonly item: ChartLegendItem;
}): React.ReactElement {
  const marker = item.marker ?? "line";
  if (marker === "dot") {
    return item.tint !== undefined ? (
      <StatusDot tint={item.tint} />
    ) : (
      <StatusDot tone={item.tone} />
    );
  }
  const colour =
    item.tint !== undefined ? tintClasses(item.tint) : TONES[item.tone];
  return (
    <span
      aria-hidden="true"
      className={cn(
        marker === "dashed" ? DASHED : LINE,
        marker === "dashed" ? colour.dotBorder : colour.dot,
      )}
    />
  );
}

export const ChartLegend = forwardRef<HTMLUListElement, ChartLegendProps>(
  function ChartLegend({ label, items, className, ...rest }, ref) {
    return (
      <ul
        ref={ref}
        {...rest}
        aria-label={label}
        className={cn("flex flex-wrap gap-x-5 gap-y-2", className)}
      >
        {items.map((item) => (
          <li
            key={item.id}
            className="inline-flex items-center gap-2 text-xs text-pb-text-secondary"
          >
            <LegendMarker item={item} />
            {item.label}
          </li>
        ))}
      </ul>
    );
  },
);
